import React, { useCallback, useEffect, useState } from 'react';
import api from '../../utils/api';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import DateInput from '../ui/date-input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { useToast } from '../../hooks/use-toast';
import { CRM_FINANCIAL_YEAR_KEY } from '../../hooks/useCrmFinancialYear';
import { Loader2 } from 'lucide-react';

const PRIMARY_BTN = 'bg-blue-600 hover:bg-blue-700 text-white';

function notifyFinancialYearChanged() {
  window.dispatchEvent(new CustomEvent('crmFinancialYearChanged', { detail: { refresh: true } }));
}

export default function PlatformFinancialYearSettings() {
  const { toast } = useToast();
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingYearId, setUpdatingYearId] = useState(null);
  const [deactivatingYearId, setDeactivatingYearId] = useState(null);
  const [yearForm, setYearForm] = useState({ name: '', start_date: '', end_date: '' });

  const fetchYears = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/crm/financial-years?all=1');
      setYears(res.data?.data || []);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message || 'Failed to load financial years', variant: 'destructive' });
      setYears([]);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { fetchYears(); }, [fetchYears]);

  const handleCreateYear = async (e) => {
    e.preventDefault();
    const existing = years.find((y) => y.name === yearForm.name && y.status !== 'inactive');
    if (existing) {
      toast({ title: 'Already exists', description: 'This financial year already exists.', variant: 'destructive' });
      return;
    }
    const hasCurrent = years.some((y) => y.status === 'active' && (y.is_current === true || y.is_current === 1 || String(y.is_current) === '1'));
    try {
      await api.post('/crm/financial-years', { ...yearForm, is_current: !hasCurrent });
      toast({ title: 'Success', description: 'Financial year created' });
      setYearForm({ name: '', start_date: '', end_date: '' });
      await fetchYears();
      notifyFinancialYearChanged();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message || 'Failed to create financial year', variant: 'destructive' });
    }
  };

  const handleSetCurrentYear = async (yearId) => {
    if (!yearId || updatingYearId) return;
    setUpdatingYearId(yearId);
    try {
      await api.put(`/crm/financial-years/${yearId}/current`);
      setYears((prev) => prev.map((y) => ({
        ...y,
        is_current: String(y.id) === String(yearId) ? 1 : 0,
      })));
      toast({ title: 'Success', description: 'Financial year updated' });
      notifyFinancialYearChanged();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message || 'Failed to update financial year', variant: 'destructive' });
    } finally {
      setUpdatingYearId(null);
    }
  };

  const handleDeactivateYear = async (year) => {
    const isCurrent = year.is_current === true || year.is_current === 1 || String(year.is_current) === '1';
    if (isCurrent) {
      toast({ title: 'Cannot deactivate', description: 'Set another year as current before deactivating this one.', variant: 'destructive' });
      return;
    }
    if (!window.confirm(`Deactivate financial year "${year.name}"? It will be hidden from the picker.`)) {
      return;
    }
    setDeactivatingYearId(year.id);
    try {
      await api.put(`/crm/financial-years/${year.id}/deactivate`);
      toast({ title: 'Deactivated', description: 'Financial year hidden from picker' });
      const saved = localStorage.getItem(CRM_FINANCIAL_YEAR_KEY);
      if (saved && String(saved) === String(year.id)) {
        localStorage.removeItem(CRM_FINANCIAL_YEAR_KEY);
      }
      await fetchYears();
      notifyFinancialYearChanged();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message || 'Failed to deactivate financial year', variant: 'destructive' });
    } finally {
      setDeactivatingYearId(null);
    }
  };

  const activeYears = years.filter((y) => y.status !== 'inactive');

  return (
    <div data-testid="crm-financial-years">
      <Card className="rounded-2xl border-border shadow-sm">
        <CardContent className="p-6">
          <form onSubmit={handleCreateYear} className="space-y-4 mb-6 p-4 rounded-xl bg-muted/30 border border-border">
            <p className="text-xs text-muted-foreground">Add one financial year at a time (e.g. Apr 1 – Mar 31).</p>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={yearForm.name}
                  onChange={(e) => setYearForm({ ...yearForm, name: e.target.value })}
                  placeholder="2025–2026"
                  className="mt-1.5 rounded-lg"
                  required
                />
              </div>
              <div>
                <Label>Start Date</Label>
                <DateInput value={yearForm.start_date} onChange={(v) => setYearForm({ ...yearForm, start_date: v })} className="mt-1.5 rounded-lg" required />
              </div>
              <div>
                <Label>End Date</Label>
                <DateInput value={yearForm.end_date} onChange={(v) => setYearForm({ ...yearForm, end_date: v })} className="mt-1.5 rounded-lg" required />
              </div>
            </div>
            <Button type="submit" className={PRIMARY_BTN}>Add Year</Button>
          </form>

          {loading ? (
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading financial years…
            </p>
          ) : years.length === 0 ? (
            <p className="text-sm text-muted-foreground">No financial years yet. Create one to use in CRM reports.</p>
          ) : (
            <div className="space-y-2">
              {years.map((year) => {
                const isCurrent = year.is_current === true || year.is_current === 1 || String(year.is_current) === '1';
                const isInactive = year.status === 'inactive';
                return (
                  <div
                    key={year.id}
                    className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 rounded-xl border border-border ${isInactive ? 'bg-muted/10 opacity-70' : 'bg-muted/20'}`}
                  >
                    <div>
                      <p className="font-medium text-foreground">{year.name}</p>
                      <p className="text-sm text-muted-foreground">{year.start_date} – {year.end_date}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      {isInactive && (
                        <span className="px-2 py-1 bg-muted text-muted-foreground rounded-full text-xs font-medium">Inactive</span>
                      )}
                      {!isInactive && isCurrent && (
                        <span className="px-2 py-1 bg-green-500/15 text-green-600 dark:text-green-400 rounded-full text-xs font-medium">Current</span>
                      )}
                      {!isInactive && (
                        <Switch
                          checked={isCurrent}
                          onCheckedChange={(checked) => checked && handleSetCurrentYear(year.id)}
                          disabled={(updatingYearId && String(updatingYearId) !== String(year.id)) || isInactive}
                          className="data-[state=checked]:bg-blue-600"
                        />
                      )}
                      {!isInactive && !isCurrent && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeactivateYear(year)}
                          disabled={deactivatingYearId === year.id}
                        >
                          {deactivatingYearId === year.id ? 'Deactivating…' : 'Deactivate'}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {!loading && (
            <p className="text-xs text-muted-foreground mt-4">
              {activeYears.length} active year{activeYears.length === 1 ? '' : 's'} visible in the CRM financial year picker.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
